import React, { useMemo, useState } from "react"
import Icon from "../Icon"
import StepEventType from "./create/StepEventType"
import StepGroupName from "./create/StepGroupName"
import StepReview from "./create/StepReview"

const steps = [
  { id: 1, label: "Event" },
  { id: 2, label: "Name" },
  { id: 3, label: "Review" },
]

const CHAMAA_LABEL = "Chamaa (Merry-Go-Round)"
const PROJECT_SAVINGS_LABEL = "Project Savings"
const WELFARE_LABEL = "Welfare/Support group"
const normalizeProbationDraft = (value, unit) => {
  const normalizedUnit = String(unit || "").trim().toLowerCase()
  const parsedValue = Number(value)
  const unitIsValid = normalizedUnit === "days" || normalizedUnit === "months"
  const valueIsPositiveInteger = Number.isInteger(parsedValue) && parsedValue > 0
  if (!unitIsValid || !valueIsPositiveInteger) {
    return { value: "", unit: "" }
  }
  return { value: String(parsedValue), unit: normalizedUnit }
}

const buildInitialForm = () => ({
  eventTypeCategory: "",
  eventTypeNonRecurring: "",
  eventTypeRecurring: "",
  eventType: "",
  otherDescription: "",
  autoInvoicing: false,
  invoiceFrequency: "",
  invoiceStartDate: "",
  groupName: "",
  enableAKA: false,
  akaLabel: "",
  shortMode: "set",
  shortAmount: "",
  longAmount: "",
  longFrequency: "",
  chamaaMaxSharesPerMember: "1",
  chamaaFirstCycleStartDate: "",
  chamaaFirstCycleInvoiceFrequency: "",
  projectSavingsPerPersonAmount: "",
  projectSavingsClosingDate: "",
  welfareRegistrationFee: "",
  welfareEnrollmentEnabled: true,
  welfareEnrollmentStartDate: "",
  welfareEnrollmentEndDate: "",
  welfareBenefitMember: "",
  welfareBenefitSpouse: "",
  welfareBenefitSpouseParent: "",
  welfareBenefitChild: "",
  welfareBenefitSibling: "",
  welfareBenefitParent: "",
  welfareReimbursementMember: "",
  welfareReimbursementSpouse: "",
  welfareReimbursementSpouseParent: "",
  welfareReimbursementChild: "",
  welfareReimbursementSibling: "",
  welfareReimbursementParent: "",
  welfareCoverParents: false,
  welfareCoverSpouse: false,
  welfareCoverSpouseParents: false,
  welfareCoverChildren: false,
  welfareCoverSiblings: false,
  welfareProbationValue: "",
  welfareProbationUnit: "",
  lateFee: "0",
  recipient: "",
  eventDate: "",
  totalBudget: "",
  amountPerPerson: "",
  shortEventExplainer: "",
})

const buildStepOneValidation = (form) => {
  const missing = {
    eventCategory: false,
    nonRecurringEvent: false,
    nonRecurringOtherDescription: false,
    recurringEvent: false,
    recurringOtherDescription: false,
    autoInvoiceFrequency: false,
    autoInvoiceStartDate: false,
    chamaaContribution: false,
    chamaaMaxSharesPerMember: false,
    chamaaFirstCycleStartDate: false,
    chamaaFirstCycleInvoiceFrequency: false,
    projectSavingsPerPersonAmount: false,
    projectSavingsClosingDate: false,
    welfareRegistrationFee: false,
    welfareEnrollmentStartDate: false,
    welfareEnrollmentEndDate: false,
    welfareBenefitAmounts: false,
    welfareReimbursementAmounts: false,
    welfareProbationUnit: false,
    welfareProbationValue: false,
  }

  const hasNon = !!form.eventTypeNonRecurring
  const hasRec = !!form.eventTypeRecurring

  if (hasNon) {
    if (form.eventTypeNonRecurring === "Other" && !form.otherDescription.trim()) {
      missing.nonRecurringOtherDescription = true
      return { isValid: false, missing }
    }
    return { isValid: true, missing }
  }

  if (hasRec) {
    const recurringValue = String(form.eventTypeRecurring || "")
    const recurringSlug = recurringValue.toLowerCase()
    const isChamaa =
      recurringValue === CHAMAA_LABEL ||
      recurringSlug.includes("chamaa")
    const isProjectSavings =
      recurringValue === PROJECT_SAVINGS_LABEL ||
      recurringSlug.includes("project-savings") ||
      recurringSlug.includes("project savings")
    const isWelfare =
      recurringValue === WELFARE_LABEL ||
      recurringSlug.includes("welfare") ||
      recurringSlug.includes("support-group")

    if (form.eventTypeRecurring === "Others" && !form.otherDescription.trim()) {
      missing.recurringOtherDescription = true
    }

    if (isChamaa) {
      const amount = Number(form.longAmount)
      if (!Number.isFinite(amount) || amount <= 0) missing.chamaaContribution = true

      const maxShares = Number(form.chamaaMaxSharesPerMember || 1)
      if (!Number.isFinite(maxShares) || maxShares < 1) missing.chamaaMaxSharesPerMember = true

      if (!form.chamaaFirstCycleStartDate) missing.chamaaFirstCycleStartDate = true
      if (!form.chamaaFirstCycleInvoiceFrequency) missing.chamaaFirstCycleInvoiceFrequency = true
      return { isValid: !Object.values(missing).some(Boolean), missing }
    }

    if (isProjectSavings) {
      const perPersonAmount = Number(form.projectSavingsPerPersonAmount)
      if (!Number.isFinite(perPersonAmount) || perPersonAmount <= 0) {
        missing.projectSavingsPerPersonAmount = true
      }
      if (!form.projectSavingsClosingDate) missing.projectSavingsClosingDate = true
      return { isValid: !Object.values(missing).some(Boolean), missing }
    }

    if (isWelfare) {
      const registrationFee = Number(form.welfareRegistrationFee)
      if (!Number.isFinite(registrationFee) || registrationFee <= 0) {
        missing.welfareRegistrationFee = true
      }

      if (form.welfareEnrollmentEnabled && !form.welfareEnrollmentStartDate) {
        missing.welfareEnrollmentStartDate = true
      }
      if (form.welfareEnrollmentStartDate && form.welfareEnrollmentEndDate) {
        const start = new Date(`${form.welfareEnrollmentStartDate}T00:00:00`)
        const end = new Date(`${form.welfareEnrollmentEndDate}T00:00:00`)
        if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end.getTime() < start.getTime()) {
          missing.welfareEnrollmentEndDate = true
        }
      }

      const benefitValues = [
        form.welfareBenefitMember,
        form.welfareBenefitSpouse,
        form.welfareBenefitSpouseParent,
        form.welfareBenefitChild,
        form.welfareBenefitSibling,
        form.welfareBenefitParent,
      ]
      for (const value of benefitValues) {
        const parsed = Number(value)
        if (!Number.isFinite(parsed) || parsed < 0) {
          missing.welfareBenefitAmounts = true
          break
        }
      }

      const reimbursementValues = [
        form.welfareReimbursementMember,
        form.welfareReimbursementSpouse,
        form.welfareReimbursementSpouseParent,
        form.welfareReimbursementChild,
        form.welfareReimbursementSibling,
        form.welfareReimbursementParent,
      ]
      for (const value of reimbursementValues) {
        const parsed = Number(value)
        if (!Number.isFinite(parsed) || parsed < 0) {
          missing.welfareReimbursementAmounts = true
          break
        }
      }

      const probationValueRaw = String(form.welfareProbationValue || "").trim()
      const probationUnit = String(form.welfareProbationUnit || "").trim().toLowerCase()
      const probationValue = Number(probationValueRaw)
      const probationUnitProvided = ["days", "months"].includes(probationUnit)
      const probationValueProvided = probationValueRaw !== ""
      const probationValueIsZero =
        probationValueProvided && Number.isFinite(probationValue) && probationValue === 0
      const probationValueIsPositiveInteger =
        probationValueProvided && Number.isInteger(probationValue) && probationValue > 0

      // "0" is treated as "No probation".
      if (!probationValueIsZero) {
        if (probationUnitProvided && !probationValueIsPositiveInteger) {
          missing.welfareProbationValue = true
        }
        if (!probationUnitProvided && probationValueProvided) {
          missing.welfareProbationUnit = true
        }
      }

      return { isValid: !Object.values(missing).some(Boolean), missing }
    }

    if (form.autoInvoicing && (!form.invoiceFrequency || !form.invoiceStartDate)) {
      if (!form.invoiceFrequency) missing.autoInvoiceFrequency = true
      if (!form.invoiceStartDate) missing.autoInvoiceStartDate = true
      return { isValid: false, missing }
    }

    return { isValid: true, missing }
  }

  if (!form.eventTypeCategory) {
    missing.eventCategory = true
  } else if (form.eventTypeCategory === "non-recurring") {
    missing.nonRecurringEvent = true
  } else if (form.eventTypeCategory === "recurring") {
    missing.recurringEvent = true
  }

  return { isValid: false, missing }
}

export const buildStepTwoValidation = (form, requireOneTimeFields = false) => {
  const missing = {
    groupName: false,
    eventDate: false,
    totalBudget: false,
    amountPerPerson: false,
    shortEventExplainer: false,
  }

  if (!form.groupName.trim()) {
    missing.groupName = true
  }

  if (requireOneTimeFields) {
    if (!form.eventDate) {
      missing.eventDate = true
    }

    const totalBudgetRaw = String(form.totalBudget || "").trim()
    const totalBudgetValue = Number(totalBudgetRaw)
    if (!totalBudgetRaw || !Number.isFinite(totalBudgetValue) || totalBudgetValue <= 0) {
      missing.totalBudget = true
    }

    const perShareRaw = String(form.amountPerPerson || "").trim()
    if (perShareRaw) {
      const perShareValue = Number(perShareRaw)
      if (!Number.isFinite(perShareValue) || perShareValue <= 0) {
        missing.amountPerPerson = true
      }
    }

    if (!String(form.shortEventExplainer || "").trim()) {
      missing.shortEventExplainer = true
    }
  }

  return {
    isValid: !Object.values(missing).some(Boolean),
    missing,
  }
}

function GroupCreationWizard({ onClose, onCreate, isSubmitting = false, errorMessage = "", initialCategory = "" }) {
  const [activeStep, setActiveStep] = useState(1)
  const [form, setForm] = useState(() => ({ ...buildInitialForm(), eventTypeCategory: initialCategory }))
  const [stepValidationAttempts, setStepValidationAttempts] = useState({ 1: 0, 2: 0 })
  const [primaryActionShakeCount, setPrimaryActionShakeCount] = useState(0)

  const title = useMemo(() => {
    const t = form.eventType ? `${form.eventType} Group` : "Group"
    return `Create ${t}`
  }, [form.eventType])

  const isNonRecurringEvent = useMemo(
    () => form.eventTypeCategory === "non-recurring",
    [form.eventTypeCategory]
  )

  const goNext = () => {
    setActiveStep((s) => Math.min(steps.length, s + 1))
  }

  const goBack = () => setActiveStep((s) => Math.max(1, s - 1))
  const stepOneValidation = useMemo(() => buildStepOneValidation(form), [form])
  const stepTwoValidation = useMemo(
    () => buildStepTwoValidation(form, isNonRecurringEvent),
    [form, isNonRecurringEvent]
  )

  const canContinue = useMemo(() => {
    if (activeStep === 1) return stepOneValidation.isValid
    if (activeStep === 2) return stepTwoValidation.isValid
    return true
  }, [
    activeStep,
    stepOneValidation,
    stepTwoValidation,
  ])

  const handleStepAdvance = (e) => {
    e?.preventDefault()
    if (!canContinue) {
      setStepValidationAttempts((prev) => ({
        ...prev,
        [activeStep]: (prev[activeStep] || 0) + 1,
      }))
      setPrimaryActionShakeCount((prev) => prev + 1)
      return
    }
    goNext()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const normalizedProbation = normalizeProbationDraft(
      form.welfareProbationValue,
      form.welfareProbationUnit
    )

    const payload = {
      groupName: form.groupName || "Untitled Group",
      eventTypeCategory: form.eventTypeCategory,
      eventType: form.eventType || "",
      otherDescription: form.otherDescription || "",
      autoInvoicing: !!form.autoInvoicing,
      invoiceFrequency: form.invoiceFrequency || "",
      invoiceStartDate: form.invoiceStartDate || "",
      enableAKA: !!form.enableAKA,
      akaLabel: form.akaLabel || "",
      shortMode: form.shortMode,
      shortAmount: form.shortAmount,
      longAmount: form.longAmount,
      longFrequency: form.longFrequency,
      lateFee: form.lateFee,
      recipient: form.recipient,
      eventDate: isNonRecurringEvent ? form.eventDate || "" : "",
      totalBudget: isNonRecurringEvent ? form.totalBudget : "",
      amountPerPerson: isNonRecurringEvent ? form.amountPerPerson : "",
      shortEventExplainer: isNonRecurringEvent ? form.shortEventExplainer || "" : "",
      maxSharesPerMember: form.chamaaMaxSharesPerMember || "1",
      firstCycleStartDate: form.chamaaFirstCycleStartDate || "",
      firstCycleInvoiceFrequency: form.chamaaFirstCycleInvoiceFrequency || "",
      firstCycleContributionAmount: form.longAmount || "",
      firstCycleMaxSharesPerMember: form.chamaaMaxSharesPerMember || "1",
      projectSavingsPerPersonAmount: form.projectSavingsPerPersonAmount || "",
      projectSavingsClosingDate: form.projectSavingsClosingDate || "",
      welfareRegistrationFee: form.welfareRegistrationFee || "",
      welfareLateFee: "0",
      welfareEnrollmentEnabled: !!form.welfareEnrollmentEnabled,
      welfareEnrollmentStartDate: form.welfareEnrollmentStartDate || "",
      welfareEnrollmentEndDate: form.welfareEnrollmentEndDate || "",
      welfareBenefitAmounts: {
        member: form.welfareBenefitMember || "",
        spouse: form.welfareBenefitSpouse || "",
        spouse_parent: form.welfareBenefitSpouseParent || "",
        child: form.welfareBenefitChild || "",
        sibling: form.welfareBenefitSibling || "",
        parent: form.welfareBenefitParent || "",
      },
      welfareReimbursementAmounts: {
        member: form.welfareReimbursementMember || "",
        spouse: form.welfareReimbursementSpouse || "",
        spouse_parent: form.welfareReimbursementSpouseParent || "",
        child: form.welfareReimbursementChild || "",
        sibling: form.welfareReimbursementSibling || "",
        parent: form.welfareReimbursementParent || "",
      },
      welfareCoverage: {
        parent: !!form.welfareCoverParents,
        spouse: !!form.welfareCoverSpouse,
        spouseParent: !!(form.welfareCoverSpouse && form.welfareCoverSpouseParents),
        child: !!form.welfareCoverChildren,
        sibling: !!form.welfareCoverSiblings,
      },
      welfareProbationValue: normalizedProbation.value,
      welfareProbationUnit: normalizedProbation.unit,
    }

    const recurringValue = String(form.eventTypeRecurring || "")
    const recurringSlug = recurringValue.toLowerCase()
    const isChamaa =
      recurringValue === CHAMAA_LABEL ||
      recurringSlug.includes("chamaa")
    const isProjectSavings =
      recurringValue === PROJECT_SAVINGS_LABEL ||
      recurringSlug.includes("project-savings") ||
      recurringSlug.includes("project savings")
    const isWelfare =
      recurringValue === WELFARE_LABEL ||
      recurringSlug.includes("welfare") ||
      recurringSlug.includes("support-group")

    if (isChamaa) {
      payload.autoInvoicing = false
      payload.invoiceFrequency = ""
      payload.invoiceStartDate = ""
      payload.amountPerPerson = form.longAmount || form.amountPerPerson || ""
      payload.firstCycleStartDate = form.chamaaFirstCycleStartDate || ""
      payload.firstCycleInvoiceFrequency = form.chamaaFirstCycleInvoiceFrequency || ""
      payload.firstCycleContributionAmount = form.longAmount || payload.amountPerPerson || ""
      payload.maxSharesPerMember = form.chamaaMaxSharesPerMember || "1"
      payload.firstCycleMaxSharesPerMember = form.chamaaMaxSharesPerMember || "1"
      payload.totalBudget = ""
      payload.eventDate = ""
    }

    if (isProjectSavings) {
      payload.autoInvoicing = false
      payload.invoiceFrequency = ""
      payload.invoiceStartDate = ""
      payload.longAmount = ""
      payload.longFrequency = ""
      payload.totalBudget = ""
      payload.eventDate = form.projectSavingsClosingDate || ""
      payload.amountPerPerson = form.projectSavingsPerPersonAmount || ""
      payload.projectSavingsPerPersonAmount = form.projectSavingsPerPersonAmount || ""
      payload.projectSavingsGoalAmount = ""
      payload.projectSavingsClosingDate = form.projectSavingsClosingDate || ""
    }

    if (isWelfare) {
      payload.autoInvoicing = false
      payload.invoiceFrequency = ""
      payload.invoiceStartDate = ""
      payload.longAmount = ""
      payload.longFrequency = ""
      payload.lateFee = "0"
      payload.welfareLateFee = "0"
      payload.recipient = ""
      payload.totalBudget = ""
      payload.eventDate = ""
      payload.amountPerPerson = ""
      payload.projectSavingsPerPersonAmount = ""
      payload.projectSavingsGoalAmount = ""
      payload.projectSavingsClosingDate = ""
      payload.maxSharesPerMember = "1"
      payload.firstCycleMaxSharesPerMember = "1"
      payload.firstCycleStartDate = ""
      payload.firstCycleInvoiceFrequency = ""
      payload.firstCycleContributionAmount = ""
    }

    const created = await onCreate?.(payload)
    if (created) onClose?.()
  }

  const resetRecurringSpecializedFields = () => ({
    longAmount: "",
    longFrequency: "",
    chamaaMaxSharesPerMember: "1",
    chamaaFirstCycleStartDate: "",
    chamaaFirstCycleInvoiceFrequency: "",
    projectSavingsPerPersonAmount: "",
    projectSavingsClosingDate: "",
    welfareRegistrationFee: "",
    welfareEnrollmentEnabled: true,
    welfareEnrollmentStartDate: "",
    welfareEnrollmentEndDate: "",
    welfareBenefitMember: "",
    welfareBenefitSpouse: "",
    welfareBenefitSpouseParent: "",
    welfareBenefitChild: "",
    welfareBenefitSibling: "",
    welfareBenefitParent: "",
    welfareReimbursementMember: "",
    welfareReimbursementSpouse: "",
    welfareReimbursementSpouseParent: "",
    welfareReimbursementChild: "",
    welfareReimbursementSibling: "",
    welfareReimbursementParent: "",
    welfareCoverParents: false,
    welfareCoverSpouse: false,
    welfareCoverSpouseParents: false,
    welfareCoverChildren: false,
    welfareCoverSiblings: false,
    welfareProbationValue: "",
    welfareProbationUnit: "",
    lateFee: "0",
    recipient: "",
  })

  const renderStep = () => {
    if (activeStep === 1) {
      return (
        <StepEventType
          category={form.eventTypeCategory}
          nonRecurringValue={form.eventTypeNonRecurring}
          recurringValue={form.eventTypeRecurring}
          otherDescription={form.otherDescription}
          autoInvoicing={form.autoInvoicing}
          invoiceFrequency={form.invoiceFrequency}
          invoiceStartDate={form.invoiceStartDate}
          longAmount={form.longAmount}
          chamaaMaxSharesPerMember={form.chamaaMaxSharesPerMember}
          chamaaFirstCycleStartDate={form.chamaaFirstCycleStartDate}
          chamaaFirstCycleInvoiceFrequency={form.chamaaFirstCycleInvoiceFrequency}
          projectSavingsPerPersonAmount={form.projectSavingsPerPersonAmount}
          projectSavingsClosingDate={form.projectSavingsClosingDate}
          welfareRegistrationFee={form.welfareRegistrationFee}
          welfareEnrollmentEnabled={form.welfareEnrollmentEnabled}
          welfareEnrollmentStartDate={form.welfareEnrollmentStartDate}
          welfareEnrollmentEndDate={form.welfareEnrollmentEndDate}
          welfareBenefitMember={form.welfareBenefitMember}
          welfareBenefitSpouse={form.welfareBenefitSpouse}
          welfareBenefitSpouseParent={form.welfareBenefitSpouseParent}
          welfareBenefitChild={form.welfareBenefitChild}
          welfareBenefitSibling={form.welfareBenefitSibling}
          welfareBenefitParent={form.welfareBenefitParent}
          welfareReimbursementMember={form.welfareReimbursementMember}
          welfareReimbursementSpouse={form.welfareReimbursementSpouse}
          welfareReimbursementSpouseParent={form.welfareReimbursementSpouseParent}
          welfareReimbursementChild={form.welfareReimbursementChild}
          welfareReimbursementSibling={form.welfareReimbursementSibling}
          welfareReimbursementParent={form.welfareReimbursementParent}
          welfareCoverParents={form.welfareCoverParents}
          welfareCoverSpouse={form.welfareCoverSpouse}
          welfareCoverSpouseParents={form.welfareCoverSpouseParents}
          welfareCoverChildren={form.welfareCoverChildren}
          welfareCoverSiblings={form.welfareCoverSiblings}
          welfareProbationValue={form.welfareProbationValue}
          welfareProbationUnit={form.welfareProbationUnit}
          lateFee={form.lateFee}
          recipient={form.recipient}
          validation={stepOneValidation.missing}
          invalidAttemptCount={stepValidationAttempts[1] || 0}
          onChangeCategory={(cat) => {
            setForm((p) => ({
              ...p,
              eventTypeCategory: cat,
              eventTypeNonRecurring: "",
              eventTypeRecurring: "",
              eventType: "",
              otherDescription: "",
              autoInvoicing: false,
              invoiceFrequency: "",
              invoiceStartDate: "",
              ...resetRecurringSpecializedFields(),
              eventDate: "",
              totalBudget: "",
              amountPerPerson: "",
              shortEventExplainer: "",
            }))
          }}
          onChangeNonRecurring={(v) =>
            setForm((p) => ({
              ...p,
              eventTypeNonRecurring: v,
              eventTypeRecurring: "",
              eventType: v || "",
              otherDescription: v === "Other" ? p.otherDescription : "",
              autoInvoicing: false,
              invoiceFrequency: "",
              invoiceStartDate: "",
              ...resetRecurringSpecializedFields(),
            }))
          }
          onChangeRecurring={(v) =>
            setForm((p) => ({
              ...p,
              ...(v === CHAMAA_LABEL
                ? {
                    autoInvoicing: false,
                    invoiceFrequency: "",
                    invoiceStartDate: "",
                  }
                : {
                    chamaaMaxSharesPerMember: "1",
                    chamaaFirstCycleStartDate: "",
                    chamaaFirstCycleInvoiceFrequency: "",
                  }),
              ...(v === PROJECT_SAVINGS_LABEL
                ? {
                    autoInvoicing: false,
                    invoiceFrequency: "",
                    invoiceStartDate: "",
                    longAmount: "",
                    longFrequency: "",
                    lateFee: "0",
                    recipient: "",
                  }
                : {
                    projectSavingsPerPersonAmount: "",
                    projectSavingsClosingDate: "",
                  }),
              ...(v === WELFARE_LABEL
                ? {
                    autoInvoicing: false,
                    invoiceFrequency: "",
                    invoiceStartDate: "",
                    longAmount: "",
                    longFrequency: "",
                    lateFee: "0",
                    recipient: "",
                  }
                : {
                    welfareRegistrationFee: "",
                    welfareEnrollmentEnabled: true,
                    welfareEnrollmentStartDate: "",
                    welfareEnrollmentEndDate: "",
                    welfareBenefitMember: "",
                    welfareBenefitSpouse: "",
                    welfareBenefitSpouseParent: "",
                    welfareBenefitChild: "",
                    welfareBenefitSibling: "",
                    welfareBenefitParent: "",
                    welfareReimbursementMember: "",
                    welfareReimbursementSpouse: "",
                    welfareReimbursementSpouseParent: "",
                    welfareReimbursementChild: "",
                    welfareReimbursementSibling: "",
                    welfareReimbursementParent: "",
                    welfareCoverParents: false,
                    welfareCoverSpouse: false,
                    welfareCoverSpouseParents: false,
                    welfareCoverChildren: false,
                    welfareCoverSiblings: false,
                    welfareProbationValue: "",
                    welfareProbationUnit: "",
                  }),
              eventTypeRecurring: v,
              eventTypeNonRecurring: "",
              eventType: v || "",
              otherDescription: v === "Others" ? p.otherDescription : "",
              eventDate: "",
              totalBudget: "",
              amountPerPerson: "",
              shortEventExplainer: "",
            }))
          }
          onChangeOther={(v) => setForm((p) => ({ ...p, otherDescription: v }))}
          onChangeAutoInvoicing={(v) => setForm((p) => ({ ...p, autoInvoicing: v }))}
          onChangeInvoiceFrequency={(v) =>
            setForm((p) => ({
              ...p,
              invoiceFrequency: v,
              longFrequency: v,
              longAmount: v === "As Needed" || v === "Is Needed" ? "" : p.longAmount,
            }))
          }
          onChangeInvoiceStartDate={(v) => setForm((p) => ({ ...p, invoiceStartDate: v }))}
          onChangeLongAmount={(v) => setForm((p) => ({ ...p, longAmount: v }))}
          onChangeChamaaMaxSharesPerMember={(v) =>
            setForm((p) => ({ ...p, chamaaMaxSharesPerMember: v }))
          }
          onChangeChamaaFirstCycleStartDate={(v) =>
            setForm((p) => ({ ...p, chamaaFirstCycleStartDate: v }))
          }
          onChangeChamaaFirstCycleInvoiceFrequency={(v) =>
            setForm((p) => ({ ...p, chamaaFirstCycleInvoiceFrequency: v }))
          }
          onChangeProjectSavingsPerPersonAmount={(v) =>
            setForm((p) => ({ ...p, projectSavingsPerPersonAmount: v }))
          }
          onChangeProjectSavingsClosingDate={(v) =>
            setForm((p) => ({ ...p, projectSavingsClosingDate: v }))
          }
          onChangeWelfareRegistrationFee={(v) =>
            setForm((p) => ({ ...p, welfareRegistrationFee: v }))
          }
          onChangeWelfareEnrollmentEnabled={(v) =>
            setForm((p) => ({ ...p, welfareEnrollmentEnabled: !!v }))
          }
          onChangeWelfareEnrollmentStartDate={(v) =>
            setForm((p) => ({ ...p, welfareEnrollmentStartDate: v }))
          }
          onChangeWelfareEnrollmentEndDate={(v) =>
            setForm((p) => ({ ...p, welfareEnrollmentEndDate: v }))
          }
          onChangeWelfareBenefitMember={(v) =>
            setForm((p) => ({ ...p, welfareBenefitMember: v }))
          }
          onChangeWelfareBenefitSpouse={(v) =>
            setForm((p) => ({ ...p, welfareBenefitSpouse: v }))
          }
          onChangeWelfareBenefitSpouseParent={(v) =>
            setForm((p) => ({ ...p, welfareBenefitSpouseParent: v }))
          }
          onChangeWelfareBenefitChild={(v) =>
            setForm((p) => ({ ...p, welfareBenefitChild: v }))
          }
          onChangeWelfareBenefitSibling={(v) =>
            setForm((p) => ({ ...p, welfareBenefitSibling: v }))
          }
          onChangeWelfareBenefitParent={(v) =>
            setForm((p) => ({ ...p, welfareBenefitParent: v }))
          }
          onChangeWelfareReimbursementMember={(v) =>
            setForm((p) => ({ ...p, welfareReimbursementMember: v }))
          }
          onChangeWelfareReimbursementSpouse={(v) =>
            setForm((p) => ({ ...p, welfareReimbursementSpouse: v }))
          }
          onChangeWelfareReimbursementSpouseParent={(v) =>
            setForm((p) => ({ ...p, welfareReimbursementSpouseParent: v }))
          }
          onChangeWelfareReimbursementChild={(v) =>
            setForm((p) => ({ ...p, welfareReimbursementChild: v }))
          }
          onChangeWelfareReimbursementSibling={(v) =>
            setForm((p) => ({ ...p, welfareReimbursementSibling: v }))
          }
          onChangeWelfareReimbursementParent={(v) =>
            setForm((p) => ({ ...p, welfareReimbursementParent: v }))
          }
          onChangeWelfareCoverParents={(v) =>
            setForm((p) => ({ ...p, welfareCoverParents: !!v }))
          }
          onChangeWelfareCoverSpouse={(v) =>
            setForm((p) => ({
              ...p,
              welfareCoverSpouse: !!v,
              welfareCoverSpouseParents: !!v ? p.welfareCoverSpouseParents : false,
            }))
          }
          onChangeWelfareCoverSpouseParents={(v) =>
            setForm((p) => ({ ...p, welfareCoverSpouseParents: !!v }))
          }
          onChangeWelfareCoverChildren={(v) =>
            setForm((p) => ({ ...p, welfareCoverChildren: !!v }))
          }
          onChangeWelfareCoverSiblings={(v) =>
            setForm((p) => ({ ...p, welfareCoverSiblings: !!v }))
          }
          onChangeWelfareProbationValue={(v) =>
            setForm((p) => {
              const parsed = Number(v)
              // "0" is treated as "No probation".
              if (String(v || "").trim() !== "" && Number.isFinite(parsed) && parsed === 0) {
                return { ...p, welfareProbationValue: "", welfareProbationUnit: "" }
              }
              return { ...p, welfareProbationValue: v }
            })
          }
          onChangeWelfareProbationUnit={(v) =>
            setForm((p) => ({
              ...p,
              welfareProbationUnit: v,
              welfareProbationValue: v ? p.welfareProbationValue : "",
            }))
          }
          onChangeLongFrequency={(v) => setForm((p) => ({ ...p, longFrequency: v }))}
          onChangeLateFee={(v) => setForm((p) => ({ ...p, lateFee: v }))}
          onChangeRecipient={(v) => setForm((p) => ({ ...p, recipient: v }))}
        />
      )
    }

    if (activeStep === 2) {
      return (
        <StepGroupName
          name={form.groupName}
          eventDate={form.eventDate}
          totalBudget={form.totalBudget}
          amountPerPerson={form.amountPerPerson}
          shortEventExplainer={form.shortEventExplainer}
          showOneTimeFields={isNonRecurringEvent}
          validation={stepTwoValidation.missing}
          invalidAttemptCount={stepValidationAttempts[2] || 0}
          onChangeName={(v) => setForm((p) => ({ ...p, groupName: v }))}
          onChangeEventDate={(v) => setForm((p) => ({ ...p, eventDate: v }))}
          onChangeTotalBudget={(v) => setForm((p) => ({ ...p, totalBudget: v }))}
          onChangeAmountPerPerson={(v) => setForm((p) => ({ ...p, amountPerPerson: v }))}
          onChangeShortEventExplainer={(v) =>
            setForm((p) => ({ ...p, shortEventExplainer: v }))
          }
        />
      )
    }

    if (activeStep === 3) {
      return <StepReview form={form} />
    }

    return null
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-[130]">
      <div className="h-full w-full flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div className="bg-white w-full sm:max-w-xl sm:rounded-2xl shadow-xl overflow-hidden flex flex-col h-[92vh] sm:h-auto sm:max-h-[90vh] rounded-t-2xl">
          <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-gray-200">
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 truncate">{title}</h2>
              <p className="text-xs text-gray-500 truncate">Step-by-step setup</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <Icon name="x" size="1.5rem" />
            </button>
          </div>

          <div className="px-4 sm:px-5 pt-3 pb-2 border-b border-gray-100">
            <div className="grid grid-cols-3 gap-2">
              {steps.map((step) => {
                const isActive = step.id === activeStep
                const isCompleted = step.id < activeStep
                return (
                  <button
                    key={step.id}
                    type="button"
                    onClick={() => setActiveStep(step.id)}
                    className={`px-2 py-2 rounded-xl border text-[11px] font-semibold transition-colors ${
                      isActive
                        ? "bg-sky-400 text-white border-sky-400"
                        : isCompleted
                          ? "bg-sky-50 text-sky-600 border-sky-200"
                          : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {step.id}. {step.label}
                  </button>
                )
              })}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4">{renderStep()}</div>

            <div className="px-4 sm:px-5 py-3 border-t border-gray-100 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={activeStep === 1 ? onClose : goBack}
                className="px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100"
              >
                {activeStep === 1 ? "Cancel" : "Back"}
              </button>

              <button
                type={activeStep === steps.length ? "submit" : "button"}
                disabled={isSubmitting}
                onClick={activeStep === steps.length ? undefined : handleStepAdvance}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white bg-sky-400 hover:bg-sky-500 disabled:opacity-60 disabled:cursor-not-allowed ${
                  !canContinue && activeStep !== steps.length && primaryActionShakeCount > 0
                    ? primaryActionShakeCount % 2 === 0
                      ? "animate-field-shake-a"
                      : "animate-field-shake-b"
                    : ""
                }`}
              >
                {activeStep === steps.length ? (
                  <>
                    <Icon name="check-circle" size="1rem" />
                    {isSubmitting ? "Creating..." : "Create"}
                  </>
                ) : (
                  <>
                    Next
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12,5 19,12 12,19" />
                    </svg>
                  </>
                )}
              </button>
            </div>
            {!!errorMessage && <div className="mt-3 text-xs text-red-600">{errorMessage}</div>}
          </form>
        </div>
      </div>
    </div>
  )
}

export default GroupCreationWizard
